/*
  R2R Extraction Fan & Carbon Filter Calculator.

  Sizes a grow-tent extraction system and recommends a fan, a carbon filter and
  ducting that actually fit each other.

  The commercially important rule lives in pickFilter(): a carbon filter must be
  rated ABOVE the fan driving it. A filter rated at or below the fan chokes it,
  the tent loses negative pressure and the smell escapes. Recommending a matched
  pair is the single most common mistake in this category and the main source of
  returns, so the tool applies a 25% headroom margin and refuses to pair down.

  All product data is rendered into the page by Liquid at load. Nothing is
  fetched, so results are instant and the tool works with the shop's live prices.
*/
if (!customElements.get('extraction-calculator')) {
  customElements.define('extraction-calculator', class extends HTMLElement {

    connectedCallback() {
      this.T = window.R2RTools;
      this.data = this.readData();
      if (!this.data) return;

      this.debug = new URLSearchParams(location.search).has('debug');
      this.started = false;

      this.defaults = {
        w: 120, d: 120, h: 200,
        watts: 600, light: 'led',
        filter: true, temp: 'normal',
        duct: 3, bends: 2, quiet: false
      };

      // Query string wins, then a tent size handed over by another tool, then
      // the defaults. Order matters: an explicitly shared link must never be
      // overwritten by a stale session value.
      var shared = this.T.readShared();
      var seeded = Object.assign({}, this.defaults);
      ['w', 'd', 'h'].forEach(function (k) {
        if (shared[k]) seeded[k] = shared[k];
      });
      if (shared.watts) seeded.watts = shared.watts;
      this.state = Object.assign(seeded, this.T.readState(seeded));

      this.cacheNodes();
      this.bind();
      this.syncInputs();
      this.render();
    }

    /* ---------------- data ---------------- */

    /* The section emits one flat array of every spec-bearing variant, each
       carrying its own role. Bucketing here rather than in Liquid keeps the
       template to a single loop and means a product whose vent_role is later
       corrected moves slot without a theme change. */
    readData() {
      var el = this.querySelector('[data-tool-products]');
      if (!el) return null;
      var raw;
      try { raw = JSON.parse(el.textContent); }
      catch (e) {
        console.warn('[extraction-calculator] product data failed to parse', e);
        return null;
      }
      var items = (raw && raw.items) || [];
      // A product can sit in more than one source collection (a silenced fan is
      // in both Extraction fans and Silencers), so the same variant can arrive
      // twice. First occurrence wins.
      var seen = {};
      items = items.filter(function (i) {
        if (seen[i.vid]) return false;
        seen[i.vid] = true;
        return true;
      });
      var by = function (role) {
        return items.filter(function (i) { return i.role === role; });
      };
      return {
        all: items,
        fans: by('fan'),
        filters: by('filter'),
        ducting: by('ducting'),
        silencers: by('silencer')
      };
    }

    cacheNodes() {
      this.$ = {
        presets:   this.querySelector('[data-presets]'),
        w:         this.querySelector('#ec-w'),
        d:         this.querySelector('#ec-d'),
        h:         this.querySelector('#ec-h'),
        watts:     this.querySelector('#ec-watts'),
        wattsOut:  this.querySelector('[data-watts-out]'),
        light:     this.querySelector('[data-light]'),
        temp:      this.querySelector('[data-temp]'),
        filter:    this.querySelector('#ec-filter'),
        quiet:     this.querySelector('#ec-quiet'),
        duct:      this.querySelector('#ec-duct'),
        bends:     this.querySelector('#ec-bends'),
        headline:  this.querySelector('[data-headline]'),
        ductOut:   this.querySelector('[data-duct-out]'),
        filterOut: this.querySelector('[data-filter-out]'),
        acmOut:    this.querySelector('[data-acm-out]'),
        working:   this.querySelector('[data-working]'),
        results:   this.querySelector('[data-results]'),
        notice:    this.querySelector('[data-notice]'),
        slots: {
          fan:      this.querySelector('[data-slot="fan"]'),
          filter:   this.querySelector('[data-slot="filter"]'),
          ducting:  this.querySelector('[data-slot="ducting"]'),
          silencer: this.querySelector('[data-slot="silencer"]')
        },
        template:  this.querySelector('[data-card-template]'),
        permalink: this.querySelector('[data-permalink]'),
        copyBtn:   this.querySelector('[data-copy]')
      };
    }

    bind() {
      var self = this;
      var recalc = function () { self.onInput(); };

      this.presetGroup = this.T.bindRadioGroup(this.$.presets, function (value) {
        if (value === 'custom') { self.render(); return; }
        var parts = value.split('x').map(Number);
        self.state.w = parts[0]; self.state.d = parts[1]; self.state.h = parts[2];
        self.syncInputs();
        self.onInput();
      });

      this.lightGroup = this.T.bindRadioGroup(this.$.light, function (v) {
        self.state.light = v; self.onInput();
      });
      this.tempGroup = this.T.bindRadioGroup(this.$.temp, function (v) {
        self.state.temp = v; self.onInput();
      });

      ['w', 'd', 'h', 'duct', 'bends'].forEach(function (k) {
        self.$[k].addEventListener('input', function () {
          self.state[k] = parseFloat(self.$[k].value);
          if (['w', 'd', 'h'].indexOf(k) > -1) self.markCustomPreset();
          recalc();
        });
      });

      this.$.watts.addEventListener('input', function () {
        self.state.watts = parseInt(self.$.watts.value, 10);
        self.$.wattsOut.textContent = self.state.watts + 'W';
        recalc();
      });

      this.$.filter.addEventListener('change', function () {
        self.state.filter = self.$.filter.checked; self.onInput();
      });
      this.$.quiet.addEventListener('change', function () {
        self.state.quiet = self.$.quiet.checked; self.onInput();
      });

      if (this.$.copyBtn) {
        this.$.copyBtn.addEventListener('click', function () {
          self.T.copy(self.$.permalink.value).then(function () {
            var b = self.$.copyBtn;
            var old = b.textContent;
            b.textContent = 'Copied';
            setTimeout(function () { b.textContent = old; }, 1800);
            self.T.track('tool_result_shared', { tool_name: 'extraction_calculator' });
          }).catch(function () {
            self.$.permalink.select();
          });
        });
      }

      // Recommendation clicks are delegated, because the cards are replaced on
      // every recalculation.
      this.addEventListener('click', function (e) {
        var link = e.target.closest('[data-card-link]');
        if (link) {
          self.T.track('product_clicked', {
            tool_name: 'extraction_calculator',
            recommended_product: link.dataset.handle,
            tier: link.dataset.tier,
            slot: link.dataset.slot
          });
          return;
        }
        var related = e.target.closest('[data-related-tool]');
        if (related) {
          self.T.track('related_tool_clicked', {
            tool_name: 'extraction_calculator',
            destination_tool: related.dataset.relatedTool
          });
        }
      });

      // product-form dispatches no success event, so the add is observed by
      // listening for the submit that product-form.js has already accepted.
      this.addEventListener('submit', function (e) {
        var form = e.target.closest('form');
        if (!form) return;
        var card = form.closest('[data-card]');
        if (!card) return;
        self.T.track('add_to_cart_from_tool', {
          tool_name: 'extraction_calculator',
          recommended_product: card.dataset.handle,
          tier: card.dataset.tier,
          slot: card.dataset.slot,
          value: parseFloat(card.dataset.price || '0')
        });
      }, true);

      var news = this.querySelector('[data-tool-newsletter] form');
      if (news) {
        news.addEventListener('submit', function () {
          self.T.track('tool_email_capture', {
            tool_name: 'extraction_calculator',
            result_type: 'ventilation_plan'
          });
        });
      }

      this.trackResult = this.T.debounce(function (r) {
        self.T.track('tool_result', {
          tool_name: 'extraction_calculator',
          result_type: 'extraction_m3h',
          result_value: r.required,
          tent_size: self.state.w + 'x' + self.state.d + 'x' + self.state.h,
          estimated_spend: r.spend
        });
      }, 1000);
    }

    markCustomPreset() {
      var match = this.state.w + 'x' + this.state.d + 'x' + this.state.h;
      var found = false;
      this.presetGroup.buttons.forEach(function (b) {
        var on = b.dataset.value === match;
        if (on) found = true;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      if (!found) {
        var custom = this.presetGroup.buttons[this.presetGroup.buttons.length - 1];
        custom.setAttribute('aria-checked', 'true');
        custom.tabIndex = 0;
      }
    }

    syncInputs() {
      var s = this.state;
      this.$.w.value = s.w; this.$.d.value = s.d; this.$.h.value = s.h;
      this.$.watts.value = s.watts;
      this.$.wattsOut.textContent = s.watts + 'W';
      this.$.duct.value = s.duct;
      this.$.bends.value = s.bends;
      this.$.filter.checked = s.filter;
      this.$.quiet.checked = s.quiet;
      if (this.lightGroup) {
        this.lightGroup.buttons.forEach(function (b) {
          var on = b.dataset.value === s.light;
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
      }
      if (this.tempGroup) {
        this.tempGroup.buttons.forEach(function (b) {
          var on = b.dataset.value === s.temp;
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
      }
      this.markCustomPreset();
    }

    onInput() {
      if (!this.started) {
        this.started = true;
        this.T.track('tool_started', { tool_name: 'extraction_calculator' });
      }
      this.render();
    }

    /* ---------------- calculation ---------------- */

    /* Standard UK duct sizes and the airflow each carries comfortably at the
       static pressure a filter and a few bends produce. These are working
       capacities, not theoretical maxima: sizing to the theoretical figure is
       how people end up with a howling fan. */
    static get DUCTS() {
      return [
        { mm: 100, cap: 200 },
        { mm: 125, cap: 350 },
        { mm: 150, cap: 600 },
        { mm: 200, cap: 1100 },
        { mm: 250, cap: 1800 },
        { mm: 315, cap: 3200 }
      ];
    }

    calculate() {
      var s = this.state;
      var T = this.T;

      // Clamp rather than reject. Someone typing into a number field passes
      // through invalid intermediate states on the way to a valid one, and
      // blanking the result under them is hostile.
      var w = T.clamp(s.w, 30, 1000);
      var d = T.clamp(s.d, 30, 1000);
      var h = T.clamp(s.h, 60, 400);
      var watts = T.clamp(s.watts, 0, 5000);
      var runM = T.clamp(s.duct, 0, 30);
      var bends = T.clamp(s.bends, 0, 12);

      var volume = (w / 100) * (d / 100) * (h / 100);
      var base = volume * 60;                       // one air change a minute

      // Lights add heat, and heat is what the extraction actually removes. HPS
      // and CMH throw roughly twice the waste heat of an LED for the same
      // wattage, hence the two coefficients.
      var lightF = 1 + (watts / 1000) * (s.light === 'led' ? 0.25 : 0.50);
      var filterF = s.filter ? 1.35 : 1;            // carbon costs you ~35%
      var tempF = s.temp === 'warm' ? 1.25 : (s.temp === 'cool' ? 0.90 : 1);
      var ductF = Math.pow(1.03, Math.max(0, runM - 3)) * Math.pow(1.05, bends);

      var required = Math.round(base * lightF * filterF * tempF * ductF);
      var minDuct = this.constructor.DUCTS.find(function (x) { return x.cap >= required; });

      return {
        volume: volume,
        base: Math.round(base),
        lightF: lightF, filterF: filterF, tempF: tempF, ductF: ductF,
        required: required,
        minDuctMm: minDuct ? minDuct.mm : 315,
        overCapacity: !minDuct,
        filterMin: Math.round(required * 1.25),
        airChanges: volume > 0 ? (required / 60) / volume : 0,
        w: w, d: d, h: h, watts: watts
      };
    }

    /* ---------------- selection ---------------- */

    /* Closest sufficient fan per tier. Sorted by how little it overshoots, so
       the shopper is not sold a 4,250 m3/h fan for a 270 m3/h tent -- but
       anything that fits is preferred over nothing, so the 2.5x sanity band
       falls back rather than returning empty. */
    pickFans(required) {
      var eligible = this.data.fans.filter(function (f) {
        return f.avail && f.flow && f.duct && f.flow >= required;
      });
      if (!eligible.length) return { fans: [], pair: this.pickPair(required) };

      eligible.sort(function (a, b) { return (a.flow - required) - (b.flow - required); });
      var sane = eligible.filter(function (f) { return f.flow <= required * 2.5; });

      // Nothing near the right size. Offering a tiered choice here would mean
      // presenting a 1,840 m3/h fan as the 'best performance' option for a
      // 0.8m tent, which is not advice, it is upselling. Show the single
      // smallest sufficient fan and say plainly that it is oversized.
      if (!sane.length) {
        return { fans: [eligible[0]], pair: null, oversized: true };
      }

      var out = [];
      ['budget', 'recommended', 'performance'].forEach(function (tier) {
        var hit = sane.find(function (f) { return f.tier === tier; });
        if (hit && out.indexOf(hit) === -1) out.push(hit);
      });
      if (!out.length) out.push(sane[0]);
      return { fans: out, pair: null, oversized: false };
    }

    /* When one fan cannot do it, two of the largest can. Reported explicitly
       rather than silently recommending an undersized fan. */
    pickPair(required) {
      var avail = this.data.fans.filter(function (f) { return f.avail && f.flow && f.duct; });
      if (!avail.length) return null;
      var largest = avail.reduce(function (a, b) { return b.flow > a.flow ? b : a; });
      return (largest.flow * 2 >= required) ? largest : null;
    }

    /* The filter must beat the fan, not match it. Preference order: same duct
       size as the chosen fan, then anything larger that still clears the
       requirement (which needs a reducer, and says so). */
    pickFilter(fan, filterMin, fanFlowCeiling) {
      var floor = Math.max(filterMin, fanFlowCeiling || (fan ? fan.flow : 0));
      // Narrower than the fan is never acceptable, whatever the flow rating
      // says: necking a 200mm fan down to a 150mm filter throttles it and
      // undoes the whole calculation. Equal or wider only.
      var minDuct = fan ? fan.duct : 0;
      var eligible = this.data.filters.filter(function (f) {
        return f.avail && f.filter_m3h && f.filter_m3h >= floor &&
               (!minDuct || (f.duct && f.duct >= minDuct));
      });
      if (!eligible.length) return null;

      var target = fan ? fan.duct : 0;
      eligible.sort(function (a, b) {
        var da = Math.abs((a.duct || 0) - target);
        var db = Math.abs((b.duct || 0) - target);
        if (da !== db) return da - db;              // nearest flange first
        return a.filter_m3h - b.filter_m3h;         // then least over-spec
      });
      var pick = eligible[0];
      pick = Object.assign({}, pick, { needsReducer: !!(fan && pick.duct !== fan.duct) });
      return pick;
    }

    pickDucting(fan, runM) {
      if (!fan) return null;
      var pool = this.data.ducting.filter(function (x) {
        return x.avail && x.duct === fan.duct && (!x.length || x.length >= runM);
      });
      if (!pool.length) {
        pool = this.data.ducting.filter(function (x) { return x.avail && x.duct === fan.duct; });
      }
      if (!pool.length) return null;
      pool.sort(function (a, b) {
        if (!a.length !== !b.length) return a.length ? -1 : 1;  // prefer a known length
        return a.price - b.price;
      });
      return pool[0];
    }

    pickSilencer(fan) {
      if (!fan || !this.state.quiet || fan.noise === 'silenced') return null;
      var pool = this.data.silencers.filter(function (x) {
        return x.avail && (!x.duct || x.duct === fan.duct);
      });
      if (!pool.length) return null;
      pool.sort(function (a, b) { return a.price - b.price; });
      return pool[0];
    }

    /* ---------------- render ---------------- */

    render() {
      var r = this.calculate();
      var picks = this.pickFans(r.required);
      var fan = picks.fans[0] || null;
      // The shopper may buy any of the fans shown, so the filter has to clear
      // the biggest of them -- otherwise picking the performance fan silently
      // invalidates the filter recommendation sitting next to it.
      var ceiling = picks.fans.reduce(function (m, f) { return Math.max(m, f.flow || 0); }, 0);
      var filter = this.state.filter ? this.pickFilter(fan, r.filterMin, ceiling) : null;
      var ducting = this.pickDucting(fan, this.T.clamp(this.state.duct, 0, 30));
      var silencer = this.pickSilencer(fan);

      this.renderHeadline(r, fan);
      this.renderWorking(r);
      this.renderNotice(r, picks, filter);
      this.renderSlot('fan', picks.fans, r);
      this.renderSlot('filter', filter ? [filter] : [], r);
      this.renderSlot('ducting', ducting ? [ducting] : [], r);
      this.renderSlot('silencer', silencer ? [silencer] : [], r);

      var spend = [fan, filter, ducting, silencer].reduce(function (sum, p) {
        return sum + (p ? p.price : 0);
      }, 0);

      this.T.saveShared({ w: r.w, d: r.d, h: r.h, watts: r.watts, required: r.required });
      var link = this.T.writeState(this.state, this.defaults);
      if (this.$.permalink) this.$.permalink.value = link;

      this.trackResult({ required: r.required, spend: Math.round(spend) });
      if (!this._completed && r.required > 0) {
        this._completed = true;
        this.T.track('tool_completed', {
          tool_name: 'extraction_calculator',
          result_type: 'extraction_m3h',
          result_value: r.required
        });
      }
      if (this.debug) this.reportGaps();
    }

    renderHeadline(r, fan) {
      this.$.headline.textContent = r.required.toLocaleString('en-GB');
      // The duct size quoted is the one on the fan being recommended, because
      // that is the size the shopper actually has to buy. The theoretical
      // minimum is shown in the working instead.
      var mm = fan ? fan.duct : r.minDuctMm;
      this.$.ductOut.textContent = mm + 'mm';
      this.$.filterOut.textContent = r.filterMin.toLocaleString('en-GB') + ' m³/h';
      this.$.acmOut.textContent = r.airChanges.toFixed(1) + '×';
    }

    renderWorking(r) {
      var s = this.state;
      var rows = [];
      rows.push(['Tent volume',
        r.w + ' × ' + r.d + ' × ' + r.h + 'cm = ' + r.volume.toFixed(2) + ' m³']);
      rows.push(['One air change a minute',
        r.volume.toFixed(2) + ' m³ × 60 = ' + r.base.toLocaleString('en-GB') + ' m³/h']);
      rows.push([(s.light === 'led' ? 'LED' : 'HPS / CMH') + ' heat load, ' + r.watts + 'W',
        '× ' + r.lightF.toFixed(2)]);
      if (s.filter) rows.push(['Carbon filter resistance', '× 1.35']);
      if (s.temp !== 'normal') {
        rows.push([s.temp === 'warm' ? 'Warm room, above 25°C' : 'Cool room, below 18°C',
          '× ' + r.tempF.toFixed(2)]);
      }
      if (r.ductF > 1.001) {
        rows.push(['Duct run and bends', '× ' + r.ductF.toFixed(2)]);
      }
      rows.push(['Extraction needed', '<strong>' + r.required.toLocaleString('en-GB') + ' m³/h</strong>']);

      this.$.working.innerHTML = rows.map(function (row) {
        return '<div class="ec-working__row"><span>' + row[0] + '</span><span>' + row[1] + '</span></div>';
      }).join('');
    }

    renderNotice(r, picks, filter) {
      var msgs = [];
      if (!picks.fans.length && picks.pair) {
        msgs.push('Your tent needs more extraction than any single fan we stock delivers. ' +
          'Two of the ' + picks.pair.title + ' running together will cover it.');
      } else if (!picks.fans.length) {
        msgs.push('That is beyond the range this calculator covers. Get in touch and we will ' +
          'specify it properly.');
      }
      if (this.state.filter && !filter) {
        msgs.push('No carbon filter we currently stock is rated above ' +
          r.filterMin.toLocaleString('en-GB') + ' m³/h, which is what this fan needs to breathe ' +
          'freely. Ask us and we will order one in.');
      }
      if (filter && filter.needsReducer) {
        msgs.push('The filter that fits your airflow has a ' + filter.duct + 'mm flange, so you ' +
          'will need a reducer to join it to the fan.');
      }
      if (picks.oversized && picks.fans.length) {
        var f = picks.fans[0];
        msgs.push('Your tent only needs ' + r.required.toLocaleString('en-GB') + ' m³/h. The ' +
          'smallest fan we stock is rated ' + f.flow.toLocaleString('en-GB') + ' m³/h, so it is ' +
          'well oversized — run it on a speed controller rather than flat out, or it will strip ' +
          'humidity out of a tent that size.');
      }
      if (r.watts === 0) {
        msgs.push('With no lighting wattage entered this figure covers air exchange only. ' +
          'Add your light wattage for a realistic number.');
      }
      this.$.notice.innerHTML = msgs.map(function (m) {
        return '<p class="ec-notice__item">' + m + '</p>';
      }).join('');
      this.$.notice.hidden = msgs.length === 0;
    }

    renderSlot(slot, items, r) {
      var host = this.$.slots[slot];
      if (!host) return;
      var body = host.querySelector('[data-slot-body]');
      body.innerHTML = '';
      host.hidden = items.length === 0;
      if (!items.length) return;

      var self = this;
      items.forEach(function (item, i) {
        body.appendChild(self.buildCard(item, slot, r, i));
        self.T.track('product_recommended', {
          tool_name: 'extraction_calculator',
          recommended_product: item.handle,
          tier: item.tier || '',
          slot: slot,
          price: item.price
        });
      });
    }

    buildCard(item, slot, r, index) {
      var node = this.$.template.content.firstElementChild.cloneNode(true);
      var q = function (sel) { return node.querySelector(sel); };

      node.dataset.handle = item.handle;
      node.dataset.tier = item.tier || '';
      node.dataset.slot = slot;
      node.dataset.price = item.price;

      var link = q('[data-card-link]');
      link.href = item.url;
      link.dataset.handle = item.handle;
      link.dataset.tier = item.tier || '';
      link.dataset.slot = slot;

      var img = q('[data-card-img]');
      if (item.img) {
        img.src = item.img;
        img.alt = item.title;
      } else {
        img.remove();
      }

      q('[data-card-title]').textContent = item.title;
      q('[data-card-price]').innerHTML = item.pf;

      var tierEl = q('[data-card-tier]');
      var label = this.tierLabel(item.tier, slot, index);
      if (label) { tierEl.textContent = label; } else { tierEl.remove(); }

      q('[data-card-reason]').textContent = this.reason(item, slot, r);

      var vinput = q('[data-card-variant]');
      if (item.avail) {
        vinput.value = item.vid;
      } else {
        var wrap = q('[data-card-form]');
        if (wrap) wrap.remove();
      }
      return node;
    }

    tierLabel(tier, slot, index) {
      if (slot !== 'fan') return null;
      if (tier === 'budget') return 'Budget option';
      if (tier === 'performance') return 'Best performance';
      if (tier === 'recommended') return 'Recommended';
      return index === 0 ? 'Recommended' : null;
    }

    /* The sentence that makes a recommendation read as an answer rather than an
       advert. Always states the shopper's own number back to them. */
    reason(item, slot, r) {
      var need = r.required.toLocaleString('en-GB');
      if (slot === 'fan') {
        var over = Math.round(((item.flow - r.required) / r.required) * 100);
        var head = item.flow.toLocaleString('en-GB') + ' m³/h against the ' + need +
          ' m³/h you need';
        if (over >= 40) {
          return head + ' — ' + over + '% of headroom, so it runs on a lower speed and stays quiet.';
        }
        if (over <= 10) {
          return head + '. It clears your requirement, but only just: size up if you plan to add lights.';
        }
        return head + ', a ' + over + '% margin for filter resistance as the carbon loads up.';
      }
      if (slot === 'filter') {
        return 'Rated to ' + item.filter_m3h.toLocaleString('en-GB') + ' m³/h, above the fan, ' +
          'so the carbon never becomes the restriction.';
      }
      if (slot === 'ducting') {
        return item.duct + 'mm to match the fan' +
          (item.length ? ', ' + item.length + 'm covers your ' + this.state.duct + 'm run.' : '.');
      }
      if (slot === 'silencer') {
        return item.duct + 'mm inline silencer, fitted after the fan to take the edge off the noise.';
      }
      return '';
    }

    /* ?debug=1 lists what the spec layer is missing. The calculator silently
       excludes any variant without the metafields it needs, so without this the
       gaps are invisible. */
    reportGaps() {
      var gaps = [];
      this.data.fans.forEach(function (f) {
        if (!f.flow) gaps.push('fan missing custom.airflow_m3h: ' + f.handle + ' / ' + f.vtitle);
        if (!f.duct) gaps.push('fan missing custom.duct_mm: ' + f.handle + ' / ' + f.vtitle);
      });
      this.data.filters.forEach(function (f) {
        if (!f.filter_m3h) gaps.push('filter missing custom.filter_m3h: ' + f.handle + ' / ' + f.vtitle);
      });
      if (gaps.length) {
        console.group('[extraction-calculator] ' + gaps.length + ' spec gaps');
        gaps.forEach(function (g) { console.warn(g); });
        console.groupEnd();
      } else {
        console.info('[extraction-calculator] spec layer complete');
      }
    }
  });
}
